import Footer from "../layout/footer";
import Header from "../layout/header";
import Link from "next/link";
import Image from "next/image";
import JudeMoji from "@public/subdomain/careers/judemoji.png";
import IzzahMoji from "@public/subdomain/careers/izzahmoji.png";
import KeithMoji from "@public/subdomain/careers/keithmoji.png";
import RachMoji from "@public/subdomain/careers/rachmoji.png";
import RuxMoji from "@public/subdomain/careers/ruxmoji.png";
import HanisMoji from "@public/subdomain/careers/hanismoji.png";
import FildzahMoji from "@public/subdomain/careers/fildzahmoji.png";
import JoeMoji from "@public/subdomain/careers/joemoji.png";
import KevinMoji from "@public/subdomain/careers/kevinmoji.png";
import Fade from "react-reveal/Fade";
import { Trans } from "@lingui/macro";
import { useWindowWidth } from "@react-hook/window-size";
import { useRouter } from "next/router";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Link as ScrollLink } from "react-scroll";
import { positionAvailable } from "./job-available";
import { generateSubdomainPath } from "@components/layout/shared/link-props";

const CareersPage: React.FunctionComponent = () => {
  const width = useWindowWidth();
  const isHiring = positionAvailable.length > 0 ? true : false;
  const isSmallScreen = width < 768;
  const router = useRouter();
  const { locale } = router;

  return (
    <>
      <Header showNewsStacks={false} />
      <div className="bg-off-white">
        <div className="relative mx-auto pt-10 md:h-[700px] md:w-[768px] md:pt-40">
          <Fade bottom ssrReveal>
            <p className="mt-10 mb-10 block text-center font-poppins text-3xl font-bold text-primary">
              {!isHiring ? (
                <Trans>
                  Welcome to the <br />
                  cool club
                </Trans>
              ) : (
                <Trans>We're hiring</Trans>
              )}
            </p>
            <div className="relative mx-auto mb-20 w-64 text-center md:mb-52">
              <p className="mb-10 font-sen">
                {!isHiring ? (
                  <>
                    <Trans>
                      We may not have openings at the moment, but keep your eyes
                      on this space for updates! Otherwise, send your CV
                      to&nbsp;
                      <a
                        href="mailto: work@luminews.my"
                        className="text-primary underline"
                      >
                        work@luminews.my
                      </a>
                    </Trans>
                    &nbsp;
                    <Link href="/about-us">
                      <a>
                        <Trans>
                          or see
                          <br />
                          <p className="cursor-pointer text-primary underline">
                            the team behind Lumi
                          </p>
                        </Trans>
                      </a>
                    </Link>
                  </>
                ) : (
                  <Trans>
                    You might be our next big talent 👀. Check out our openings
                    to see how you can grow with us!
                  </Trans>
                )}
              </p>
              <ScrollLink
                className="mx-auto inline-flex cursor-pointer items-center rounded-lg bg-secondary px-4 py-4 font-sen text-primary2"
                activeClass="active"
                to="section1"
                spy={true}
                smooth={true}
                offset={-20}
                duration={500}
              >
                <ChevronDownIcon className="h-5 w-5 animate-bounce" />
                <Trans>Scroll</Trans>
              </ScrollLink>
            </div>
          </Fade>
          <Fade
            top
            cascade
            delay={isSmallScreen ? 1000 : 0}
            force={true}
            distance="5%"
            duration={500}
          >
            <div className="flex flex-wrap">
              <div className="left-20 top-14 basis-1/3 pt-2 md:absolute md:pt-0">
                <Image
                  src={JudeMoji}
                  priority
                  alt="judemoji"
                  width={isSmallScreen ? 100 : 120}
                  height={isSmallScreen ? 100 : 120}
                />
              </div>
              <div className="left-6 top-52 basis-1/3 md:absolute">
                <Image
                  src={RachMoji}
                  priority
                  alt="rachmoji"
                  width={isSmallScreen ? 120 : 150}
                  height={isSmallScreen ? 120 : 150}
                />
              </div>
              <div className="bottom-40 left-10 basis-1/3 md:absolute">
                <Image
                  src={KeithMoji}
                  priority
                  alt="keithmoji"
                  width={200}
                  height={200}
                />
              </div>
              <div className="right-20 top-10 basis-1/3 md:absolute">
                <Image
                  src={HanisMoji}
                  priority
                  width={170}
                  height={170}
                  alt="lumimoji"
                />
              </div>
              <div className="top-48 right-6 basis-1/3 md:absolute ">
                <Image
                  src={RuxMoji}
                  priority
                  alt="ruxmoji"
                  width={180}
                  height={180}
                />
              </div>
              <div className="bottom-36 right-16 basis-1/3 md:absolute">
                <Image
                  src={IzzahMoji}
                  priority
                  alt="izzahmoji"
                  width={isSmallScreen ? 100 : 150}
                  height={isSmallScreen ? 100 : 150}
                />
              </div>
              <div className="right-72 top-0 basis-1/3 md:absolute">
                <Image
                  src={FildzahMoji}
                  priority
                  width={200}
                  height={200}
                  alt="fildzahmoji"
                />
              </div>
              <div className="right-60 bottom-0 basis-1/3 md:absolute">
                <Image
                  src={JoeMoji}
                  priority
                  width={isSmallScreen ? 110 : 150}
                  height={isSmallScreen ? 110 : 150}
                  alt="joemoji"
                />
              </div>
              <div className="left-40 bottom-10 basis-1/3 md:absolute">
                <Image
                  src={KevinMoji}
                  priority
                  width={isSmallScreen ? 100 : 120}
                  height={isSmallScreen ? 100 : 120}
                  alt="kevinmoji"
                />
              </div>
            </div>
          </Fade>
        </div>

        {isHiring && (
          <Fade bottom cascade delay={isSmallScreen ? 1500 : 0} distance="20%">
            <div
              id="section1"
              className={`mx-auto mt-20 max-w-4xl px-4 ${
                positionAvailable.length == 1
                  ? ""
                  : "md:grid md:grid-cols-2 md:gap-4"
              }`}
            >
              {positionAvailable.map((job) => (
                <div
                  className="shadow-[#939ABC]-400 relative mb-8 rounded-xl bg-white px-6 py-6 shadow-md md:mb-0"
                  key={job.position}
                >
                  <p className="inline rounded-lg bg-green px-2 py-1 font-sen text-white">
                    {job.label}
                  </p>
                  <p className="mt-2 font-poppins text-xl font-bold">
                    {job.title[locale]}
                  </p>
                  <p className="mt-4 mb-14 break-words font-sen">
                    {job.todos.props.children[0]}
                  </p>
                  <Link
                    href={generateSubdomainPath(`/careers/${job.position}`)}
                  >
                    <a key={job.position}>
                      <button className="ml-auto flex rounded-lg bg-primary px-2 py-2 font-sen text-white">
                        <Trans>More Info</Trans>
                      </button>
                    </a>
                  </Link>
                </div>
              ))}
            </div>
          </Fade>
        )}

        <div className="mb-10 px-4 md:mb-28">
          <Fade top delay={isSmallScreen ? 2000 : 0} distance="20%">
            <p className="mt-24 mb-10 text-center font-poppins text-3xl font-bold text-primary">
              <Trans>Perks of working here</Trans>
            </p>
          </Fade>
          <Fade cascade bottom delay={500}>
            <div className="mx-auto max-w-5xl px-4 lg:grid lg:grid-cols-3 lg:gap-x-6 lg:gap-y-8">
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">‍🏠</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Hybrid working</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    We’re currently working 3 days in the office and 2 days at
                    home.
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">🏸</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Weekly activities</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    Work hard play hard with our team lunches, badminton
                    matches, & more!
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">🎂</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Birthday leave</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    Take a day off work on your birthday every year! This is in
                    addition to your annual leave.
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">🔌</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Gadget allowance</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    RM200 monthly fixed allowance for your gadget expenses. No
                    receipts needed.
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">🚗</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Parking allowance</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    RM150 a month to cover your parking and commuting expenses -
                    even if you don't drive. No questions asked.
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">👋</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Friendly peeps</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    Our awesome crew of course, what’s a better reason?
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">‍🎨</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>App subscriptions</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    RM150 per month for software subscriptions or you can sign
                    up for a course!
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">🏝</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Paid leaves</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    From 18 days of annual leave & more, we care about you
                    having a life outside!
                  </Trans>
                </p>
              </div>
              <div className="mb-6 lg:mb-0">
                <p className="mb-4 text-5xl">🏥</p>
                <p className="mb-4 font-poppins text-2xl font-bold">
                  <Trans>Healthcare</Trans>
                </p>
                <p className="font-sen text-base">
                  <Trans>
                    Take care of your health with a RM100 monthly fixed
                    allowance, on top of medical claims of up to RM200 every
                    quarter.
                  </Trans>
                </p>
              </div>
            </div>
          </Fade>
        </div>
        <Fade left>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.914358153959!2d101.67062837543435!3d3.1173561533463046!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc4918d2a67e35%3A0xc1c0990c444f4966!2sLumi%20News%20Malaysia!5e0!3m2!1sen!2smy!4v1723693454767!5m2!1sen!2smy"
            width="100%"
            height="600"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </Fade>
        <Footer />
      </div>
    </>
  );
};

export default CareersPage;
